import React, { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from "react-i18next";
import searchResultArrow from "../../../assets/icons/search-result-arrow.svg";
import SearchNoResults from "../SearchNoResults";

// Figma nodes 89:28951 / 89:26972 — Bihar Krishi Web Handoff.
// Dropdown sits 8px below the search input (detached, fully rounded) and tracks
// its width. All colors/typography/spacing route through the central rewamp
// theme (rewampTheme.js) — Typography variants for text, palette tokens for
// color, the sx spacing shorthand (8px base) for padding/gap. No hex, no
// hardcoded font-family, no px-string spacing literals.

// Single-line ellipsis — shared by the result title + subtitle.
const ELLIPSIS_SX = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const GlobalSearchResultBox = ({
  results,
  loading,
  open,
  anchorEl,
  onClose,
  selectedIndex,
  setSelectedIndex,
  flatResults,
  onClearAll,
  onActivate,
}) => {
  const { t } = useTranslation();
  const listRef = useRef(null);

  useEffect(() => {
    if (open && selectedIndex >= 0 && listRef.current) {
      const selectedItem = listRef.current.querySelector(
        `[data-index="${selectedIndex}"]`
      );
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [selectedIndex, open]);

  if (!open) return null;

  const handleItemClick = (item) => {
    onClose();
    onActivate(item);
  };

  return (
    <Paper
      elevation={0}
      component="section"
      aria-label={t("Search Results")}
      sx={{
        position: "absolute",
        // +8 → 8px gap below the input (matches the profile menu's mt:1). The
        // box is detached from the input, so it carries a full border + radius.
        top: anchorEl ? anchorEl.offsetHeight + 8 : 46,
        right: 0,
        // Match the input box exactly: the wrapper can grow wider than the
        // maxWidth-capped input, so track the input's live width (anchorEl is
        // the search box) rather than the wrapper's 100%. right:0 keeps both
        // right edges aligned.
        width: anchorEl ? anchorEl.offsetWidth : "100%",
        bgcolor: "background.paper",
        border: "0.8px solid",
        borderColor: "divider",
        boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.1)",
        borderBottomLeftRadius: "16px",
        borderBottomRightRadius: "16px",
        overflow: "hidden",
        zIndex: 1300,
        maxHeight: "450px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        ref={listRef}
        role="listbox"
        id="global-search-results"
        sx={{ overflowY: "auto", flexGrow: 1, p: 0 }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress size={24} sx={{ color: "primary.interactive" }} />
          </Box>
        ) : results.length === 0 ? (
          <SearchNoResults />
        ) : (
          results.map((group, groupIdx) => {
            const isLastGroup = groupIdx === results.length - 1;
            return (
              <Box
                key={group.category}
                sx={{
                  borderBottom: isLastGroup ? "none" : "0.8px solid",
                  borderColor: "divider",
                  pb: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2.5,
                    pt: 1,
                    pb: 0.75,
                  }}
                >
                  <Typography
                    component="h3"
                    variant="body1"
                    sx={{ color: "text.primary" }}
                  >
                    {t(group.category)}
                  </Typography>
                  {groupIdx === 0 ? (
                    // Clear All replaces the counter chip on the first category
                    // header (Figma 89:30005): uppercase, dark (#11151E →
                    // text.primary), regular weight, letter-spaced. Native
                    // button (not MUI Button, whose theme overrides force a
                    // larger/bolder font). Clears the query and resets search.
                    <Box
                      component="button"
                      type="button"
                      onClick={onClearAll}
                      sx={{
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                        p: 0,
                        typography: "overline",
                        fontWeight: 400,
                        fontSize: "0.7rem",
                        letterSpacing: "0.06em",
                        color: "text.primary",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {t("ClearAll")}
                    </Box>
                  ) : (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        bgcolor: "neutral.chipBg",
                        color: "text.textGrey",
                        borderRadius: "10px",
                        px: 1,
                        py: 0.25,
                      }}
                    >
                      {group.items.length}
                    </Typography>
                  )}
                </Box>

                <Box
                  component="ul"
                  sx={{
                    listStyle: "none",
                    p: 0,
                    m: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                  }}
                >
                  {group.items.map((item) => {
                    const itemFlatIndex = flatResults.indexOf(item);
                    const isSelected = selectedIndex === itemFlatIndex;
                    const subtitle = item?.arguments?.source || null;

                    return (
                      <Box
                        component="li"
                        key={itemFlatIndex}
                        role="option"
                        id={`search-result-${itemFlatIndex}`}
                        aria-selected={isSelected}
                        data-index={itemFlatIndex}
                        onClick={() => handleItemClick(item)}
                        onMouseEnter={() => setSelectedIndex(itemFlatIndex)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1.5,
                          px: 2.5,
                          py: 0.25,
                          cursor: "pointer",
                          bgcolor: isSelected ? "primary.softSurface" : "transparent",
                          "&:hover": { bgcolor: "primary.softSurface" },
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            component="span"
                            variant="subtitle2"
                            sx={{ display: "block", color: "primary.interactive", ...ELLIPSIS_SX }}
                          >
                            {item.title}
                          </Typography>
                          {subtitle && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                color: "text.textGrey",
                                ...ELLIPSIS_SX,
                              }}
                            >
                              {subtitle}
                            </Typography>
                          )}
                        </Box>
                        <Box
                          component="img"
                          src={searchResultArrow}
                          alt=""
                          aria-hidden="true"
                          sx={{ width: 16, height: 16, flexShrink: 0 }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Paper>
  );
};

export default GlobalSearchResultBox;
